// Pure helpers of the files list and the document panel (GED-FE-03): the rows, what the caller may
// do with a selection, version labels and plain-language messages for the document routes.
import type {
  DocumentDetailDtoOutput,
  DocumentListDtoOutputItemsItem,
  DocumentVersionDtoOutput,
} from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { apiErrorCode, isStaleVersion, type Translate } from './model';

export type DocumentRow = DocumentListDtoOutputItemsItem;
export type DocumentDetail = DocumentDetailDtoOutput;
export type DocumentVersion = DocumentVersionDtoOutput;

/** Documents are read 50 at a time ("Show more" asks for the next page). */
export const DOCUMENTS_PAGE_SIZE = 50;

/** How long the list waits between two reads while a version is being checked. */
export const SCAN_REFRESH_MS = 5000;

/** A search box wait, in milliseconds, before the text reaches the request. */
export const SEARCH_DELAY_MS = 300;

// --- What the caller may do with a document (its `_permissions`).

interface WithDocumentPermissions {
  _permissions: readonly string[];
}

export const canEditDocument = (document: WithDocumentPermissions): boolean =>
  document._permissions.includes('can_edit');
export const canDeleteDocument = (document: WithDocumentPermissions): boolean =>
  document._permissions.includes('can_delete');

/** An action is offered for a selection only when every selected document allows it. */
export const everyAllows = (
  documents: readonly WithDocumentPermissions[],
  allows: (document: WithDocumentPermissions) => boolean,
): boolean => documents.length > 0 && documents.every(allows);

// --- Sorting (the list sorts on the server).

export type DocumentSort = 'code' | 'title' | 'updatedAt';
export interface SortState {
  sort: DocumentSort;
  order: 'asc' | 'desc';
}
export const DEFAULT_SORT: SortState = { sort: 'code', order: 'asc' };

// --- Versions.

/** The version a "restored from" note points to, when it is still in the list. */
export const restoredFrom = (
  version: DocumentVersion,
  versions: readonly DocumentVersion[],
): DocumentVersion | null =>
  version.restoredFromVersionId
    ? (versions.find((other) => other.id === version.restoredFromVersionId) ?? null)
    : null;

/** Only a checked, available version that is not the current one may be restored. */
export const canRestoreVersion = (version: DocumentVersion): boolean =>
  version.status === 'available' && !version.current;

/** The first characters of a SHA-256 (the whole value is in the accessible label). */
export const shortHash = (hash: string): string => hash.slice(0, 8);

// --- Server answers.

export const isLocked = (error: unknown): boolean => apiErrorCode(error) === 'DOCUMENT_LOCKED';

/** The code of the first field error of a 422 (`VALIDATION_FAILED`), when there is one. */
export const firstFieldCode = (error: unknown): string | null =>
  error instanceof ApiError ? (error.fieldErrors[0]?.code ?? null) : null;

/** A plain-language message for an error of a document route (never the raw server text). */
export function documentErrorMessage(t: Translate, error: unknown): string {
  if (!(error instanceof ApiError)) return t('common:errors.generic');
  if (isLocked(error)) return t('errors.locked');
  if (isStaleVersion(error)) return t('errors.documentStale');
  if (error.code === 'VALIDATION_FAILED') {
    const detail = firstFieldCode(error);
    if (detail === 'VERSION_NOT_AVAILABLE') return t('errors.versionNotAvailable');
    return t('errors.invalid');
  }
  if (error.code === 'FORBIDDEN') return t('errors.documentForbidden');
  if (error.code === 'NOT_FOUND') return t('errors.documentNotFound');
  return t(`common:errors.${error.code}`, { defaultValue: t('common:errors.generic') });
}
